import { FormArray, FormGroup, ValidationErrors } from '@angular/forms';
import { SortType } from '@features/researches/models/request-data.model';
import {
  CreatorSpacePlan,
  CreatorStep,
  ResearchCreator,
  ResearchCreatorSteps,
  StepStatus
} from '@features/researches/models/research-creator.model';
import { SensorDetails } from '@features/researches/models/sensor.model';
import { isEmpty } from 'lodash';
import { Research, ResearchStatus, ResearchWithId } from '../models/research.model';

export const FINAL_REPORT_NAME = 'Final_Report';

export function mapResearchToCreator(
  research: ResearchWithId,
  companyLogoFile: File | null,
  spacePlanFileName: string,
  previousStatus: ResearchStatus = ResearchStatus.DRAFT,
  startFromFirstStep: boolean = false
): ResearchCreator {
  const details = {
    ...research.details,
    companyLogoFile,
    status: research.details && !startFromFirstStep ? StepStatus.COMPLETED : StepStatus.IN_PROGRESS
  };
  const workspace = {
    ...research.workspace,
    status: research.workspace
      ? StepStatus.COMPLETED
      : details.status === StepStatus.COMPLETED
      ? StepStatus.IN_PROGRESS
      : StepStatus.TO_DO
  };

  const structure = {
    ...research.structure,
    status: research.structure
      ? StepStatus.COMPLETED
      : workspace.status === StepStatus.COMPLETED
      ? StepStatus.IN_PROGRESS
      : StepStatus.TO_DO
  };

  const utilization = {
    ...research.utilization,
    status: research.utilization
      ? StepStatus.COMPLETED
      : structure.status === StepStatus.COMPLETED
      ? StepStatus.IN_PROGRESS
      : StepStatus.TO_DO
  };

  const plan: CreatorSpacePlan = {
    status: !!spacePlanFileName ? StepStatus.COMPLETED : StepStatus.TO_DO,
    spacePlanFileName,
    spacePlanFileValid: !!spacePlanFileName
  };
  return {
    previousStatus,
    details: details,
    workspace: workspace,
    structure: structure,
    utilization: utilization,
    plan: plan
  };
}

export function getTeamNameById(id: string, research: Research): string {
  return (
    research.structure?.departments.flatMap((department) => department.teams).find((team) => team.externalId === id)
      ?.name || ''
  );
}

export function getFloorNameById(id: string, research: Research): string {
  return research.workspace?.availableFloors.find((floor) => floor.externalId === id)?.name || '';
}

export function mapResearchToSensorDataForUtilisedSpace(research: Research): SensorDetails[] {
  const sensorsForUtilisedSpace: SensorDetails[] = [];
  research.utilization?.byTeams.forEach((teamLocation) => {
    const teamName = getTeamNameById(teamLocation.teamExternalId, research);
    teamLocation.locations.forEach((location) => {
      const floor = getFloorNameById(location.floorExternalId, research);
      const spaceType = location.spaceType;
      if (location.sensorData) {
        Object.entries(location.sensorData).forEach(([seatId, sensor]) => {
          sensorsForUtilisedSpace.push({
            spaceName: teamName,
            macAddress: sensor?.macAddress,
            id: seatId,
            status: sensor?.status,
            floor: floor,
            spaceType: spaceType,
            isAdditionalRoom: false
          });
        });
      }
    });
  });
  return sensorsForUtilisedSpace;
}

export function mapResearchToSensorDataForAdditionalRooms(research: Research): SensorDetails[] {
  const sensorsForAdditionalRooms: SensorDetails[] = [];
  research.utilization?.additionalRooms.forEach((roomData) => {
    sensorsForAdditionalRooms.push({
      spaceName: roomData.name,
      macAddress: roomData?.sensor?.macAddress,
      id: roomData.externalId,
      status: roomData?.sensor?.status,
      floor: getFloorNameById(roomData.floorExternalId, research),
      spaceType: roomData.spaceType,
      isAdditionalRoom: true
    });
  });
  return sensorsForAdditionalRooms;
}

export function modifyNumber(n: number): string {
  if (n >= 0 && n <= 9) {
    return '00' + n;
  } else if (n > 9 && n <= 99) {
    return '0' + n;
  } else {
    return n.toString();
  }
}

export function mapResearchCreatorToResearch(researchCreator: ResearchCreator, draft: boolean): Research {
  const { status: detailsStatus, ...details } = { ...researchCreator.details, draft };
  const { status: workspaceStatus, ...workspace } = researchCreator.workspace;
  const { status: structureStatus, ...structure } = researchCreator.structure;
  const { status: utilizationStatus, ...utilization } = researchCreator.utilization;

  return {
    details: isEmpty(details) ? null : details,
    workspace: isEmpty(workspace) ? null : workspace,
    structure: isEmpty(structure) ? null : structure,
    utilization: isEmpty(utilization) ? null : utilization,
    companyLogoFileName: researchCreator.details.companyLogoFile?.name
  } as Research;
}

export function mapResearchCreatorToResearchWithId(researchCreator: ResearchCreator, draft: boolean): ResearchWithId {
  const { status: detailsStatus, ...details } = { ...researchCreator.details, draft };
  const { status: workspaceStatus, ...workspace } = researchCreator.workspace;
  const { status: structureStatus, ...structure } = researchCreator.structure;
  const { status: utilizationStatus, ...utilization } = researchCreator.utilization;

  return {
    details: isEmpty(details) ? null : details,
    workspace: isEmpty(workspace) ? null : workspace,
    structure: isEmpty(structure) ? null : structure,
    utilization: isEmpty(utilization) ? null : utilization,
    companyLogoFileName: researchCreator.details.companyLogoFile?.name
  } as ResearchWithId;
}

export function getFirstStepInProgress(steps: ResearchCreatorSteps): CreatorStep {
  if (isStepInProgress(steps.details.status)) {
    return CreatorStep.DETAILS;
  } else if (isStepInProgress(steps.workspace.status)) {
    return CreatorStep.WORKSPACE;
  } else if (isStepInProgress(steps.structure.status)) {
    return CreatorStep.STRUCTURE;
  } else if (isStepInProgress(steps.utilization.status)) {
    return CreatorStep.UTILIZATION;
  } else {
    return CreatorStep.PLAN;
  }
}

export function isStepInProgress(stepStatus: StepStatus): boolean {
  return stepStatus === StepStatus.IN_PROGRESS;
}

export function updateTreeValidity(group: FormGroup | FormArray): void {
  Object?.keys(group.controls).forEach((key: string) => {
    const abstractControl = (group as { [key: string]: any }).controls[key];

    if (abstractControl instanceof FormGroup || abstractControl instanceof FormArray) {
      updateTreeValidity(abstractControl);
    } else {
      abstractControl.updateValueAndValidity();
    }
  });
}

export function addValidatorToTree(group: FormGroup | FormArray, validator: ValidationErrors | null): void {
  Object?.keys(group?.controls).forEach((key: string) => {
    const abstractControl = (group as { [key: string]: any }).controls[key];

    if (abstractControl instanceof FormGroup || abstractControl instanceof FormArray) {
      addValidatorToTree(abstractControl, validator);
    } else {
      abstractControl.addValidators(validator);
    }
  });
}

export function removeValidatorFromTree(group: FormGroup | FormArray, validator: ValidationErrors | null): void {
  Object?.keys(group.controls).forEach((key: string) => {
    const abstractControl = (group as { [key: string]: any }).controls[key];

    if (abstractControl instanceof FormGroup || abstractControl instanceof FormArray) {
      removeValidatorFromTree(abstractControl, validator);
    } else {
      abstractControl.removeValidators(validator);
    }
  });
}

export function getColumnSortType(columnName: string): SortType {
  switch (columnName) {
    case 'id':
      return SortType.ID;
    case 'name':
      return SortType.NAME;
    case 'start':
      return SortType.START;
    case 'end':
      return SortType.END;
    case 'companyName':
      return SortType.COMPANY;
    default:
      return SortType.START;
  }
}

export function checkIfNumberTyped(event: KeyboardEvent): boolean {
  if (event.keyCode === 229 && event.key === 'Unidentified') {
    return true;
  } else if (event.key.length > 1 || ('0' <= event.key && event.key <= '9')) {
    return true;
  }
  event.preventDefault();
  return false;
}

export function isSpacePlanFileSizeValid(file: File): boolean {
  return !!file && file.size <= 25000000;
}
